import React from 'react';
import { DateTime } from 'luxon';
import md5 from 'md5';
import moment from 'moment';
import jwt from 'jsonwebtoken';
import S3 from 'aws-s3';
import { v4 as uuid } from 'uuid';
import { pdf } from '@react-pdf/renderer';
import Swal from 'sweetalert2';
import * as linkify from 'linkifyjs';
import InspectionPDF from '../PdfPreviewComp/NewInspection';
import EstimatePDF from '../PDFs/Estimate';
import WorkOrderPDF from '../PDFs/WorkOrder';
import InvoicePDF from '../PDFs/Invoice';
import QuickCheckout from '../PDFs/QuickCheckout';
import EstimatorPDF from '../PDFs/Estimator';
import store from '../../store/store';
import { getCurrencySymbol } from '../../utils/getCurrencySymbol';
import { addMediaAttachments } from '../../actions/repairOrder/ro-actions';
import { Toast } from '../Toast/Toast';
import {
  InProgressIcon,
  DeclinedIcon,
  EstimateIcon,
  InspectionIcon,
  OnHoldIcon,
  WorkNotStartedIcon,
  AwaitingAuthorizationIcon,
  AwaitingPartsIcon,
  ReadyIcon,
} from '../../constants';
import './index.scss';
import { getDefaultTerms } from '../../api/repairOrder/ro-api';

const addCommasToNumber = (number) => {
  number = +number || 0;
  return number.toLocaleString('en-US');
};

const makingLabourTimeGuideData = (laborsList) => {
  let laborTimeGuideCost = 0;

  if (laborsList?.length > 0) {
    laborsList?.map((labor) => {
      labor.id = labor['_id'] || labor.id;
      laborTimeGuideCost += +labor.total || 0;
    });
  }
  return { laborTimeGudes: laborsList, laborTimeGuideCost };
};

const getTimezone = () => {
  let timeZone = '';

  const userData = JSON.parse(localStorage.getItem('userData'));
  if (userData) {
    const { token, userStore: { generalInfo: { timeZone: tz } = {} } = {} } =
      userData;

    if (token) {
      const jwtDecodeToken = jwt.decode(token);
      timeZone = jwtDecodeToken?.timeZone || '';
    }

    if (!timeZone) {
      timeZone = tz || '';
    }
  }
  timeZone = timeZone?.trim() || '';

  return timeZone;
};

const convertSQLDateToLocalDate = (date) => {
  if (!date) return '';

  let timeZone = getTimezone();
  if (timeZone === 'America/California') timeZone = 'America/Los_Angeles'; // America/California isn't a valid timezone but America/Los_Angeles has the same offset

  let dt = '';
  if (timeZone) {
    dt = DateTime.fromISO(date).setZone(timeZone);
  }
  return dt;
};

const getCurrentDateAndTime = () => {
  let timeZone = getTimezone();
  if (timeZone === 'America/California') timeZone = 'America/Los_Angeles'; // America/California isn't a valid timezone but America/Los_Angeles has the same offset

  const now = DateTime.now().setZone(timeZone);
  if (!timeZone) return '';
  if (!now.isValid) return ''; // if time is not valid
  return now;
};

// calculating date range to show on dashboard appointments and mechanic attendance list calendar
const calculateDateRange = (currentDate) => {
  let startingIndex = 4;
  let endingIndex = 0;
  if (currentDate < 5) {
    startingIndex = 4;
    endingIndex = 0;
  } else if (currentDate < 9) {
    startingIndex = 8;
    endingIndex = 4;
  } else if (currentDate < 13) {
    startingIndex = 12;
    endingIndex = 8;
  } else if (currentDate < 17) {
    startingIndex = 16;
    endingIndex = 12;
  } else if (currentDate < 21) {
    startingIndex = 20;
    endingIndex = 16;
  } else if (currentDate < 25) {
    startingIndex = 24;
    endingIndex = 20;
  } else if (currentDate < 29) {
    startingIndex = 28;
    endingIndex = 24;
  } else if (currentDate < 32) {
    startingIndex = 32;
    endingIndex = 28;
  }
  return { startingIndex, endingIndex };
};

const decodeToken = (token) => {
  const data = {};
  const tokenData = jwt.decode(token);
  if (tokenData) {
    data.userId = tokenData.userId || '';
    data.subscriptionId = tokenData.subscriptionId || '';
    data.roleId = tokenData.roleId || '';
  }
  return data;
};

// use for vehicle plate lookup
const unitedStates = () => [
  { label: 'AL', value: 'AL', state: 'US' },
  { label: 'AK', value: 'AK', state: 'US' },
  { label: 'AZ', value: 'AZ', state: 'US' },
  { label: 'AR', value: 'AR', state: 'US' },
  { label: 'CA', value: 'CA', state: 'US' },
  { label: 'CO', value: 'CO', state: 'US' },
  { label: 'CT', value: 'CT', state: 'US' },
  { label: 'DE', value: 'DE', state: 'US' },
  { label: 'DC', value: 'DC', state: 'US' },
  { label: 'FL', value: 'FL', state: 'US' },
  { label: 'GA', value: 'GA', state: 'US' },
  { label: 'GU', value: 'GU', state: 'US' },
  { label: 'HI', value: 'HI', state: 'US' },
  { label: 'ID', value: 'ID', state: 'US' },
  { label: 'IL', value: 'IL', state: 'US' },
  { label: 'IN', value: 'IN', state: 'US' },
  { label: 'IA', value: 'IA', state: 'US' },
  { label: 'KS', value: 'KS', state: 'US' },
  { label: 'KY', value: 'KY', state: 'US' },
  { label: 'LA', value: 'LA', state: 'US' },
  { label: 'ME', value: 'ME', state: 'US' },
  { label: 'MD', value: 'MD', state: 'US' },
  { label: 'MA', value: 'MA', state: 'US' },
  { label: 'MI', value: 'MI', state: 'US' },
  { label: 'MN', value: 'MN', state: 'US' },
  { label: 'MS', value: 'MS', state: 'US' },
  { label: 'MO', value: 'MO', state: 'US' },
  { label: 'MT', value: 'MT', state: 'US' },
  { label: 'NE', value: 'NE', state: 'US' },
  { label: 'NV', value: 'NV', state: 'US' },
  { label: 'NH', value: 'NH', state: 'US' },
  { label: 'NJ', value: 'NJ', state: 'US' },
  { label: 'NM', value: 'NM', state: 'US' },
  { label: 'NY', value: 'NY', state: 'US' },
  { label: 'NC', value: 'NC', state: 'US' },
  { label: 'ND', value: 'ND', state: 'US' },
  { label: 'OH', value: 'OH', state: 'US' },
  { label: 'OK', value: 'OK', state: 'US' },
  { label: 'OR', value: 'OR', state: 'US' },
  { label: 'PA', value: 'PA', state: 'US' },
  { label: 'PR', value: 'PR', state: 'US' },
  { label: 'RI', value: 'RI', state: 'US' },
  { label: 'SC', value: 'SC', state: 'US' },
  { label: 'SD', value: 'SD', state: 'US' },
  { label: 'TN', value: 'TN', state: 'US' },
  { label: 'TX', value: 'TX', state: 'US' },
  { label: 'VI', value: 'VI', state: 'US' },
  { label: 'UT', value: 'UT', state: 'US' },
  { label: 'VT', value: 'VT', state: 'US' },
  { label: 'VA', value: 'VA', state: 'US' },
  { label: 'WA', value: 'WA', state: 'US' },
  { label: 'WV', value: 'WV', state: 'US' },
  { label: 'WI', value: 'WI', state: 'US' },
  { label: 'WY', value: 'WY', state: 'US' },
  //  canada states
  { label: 'BC', value: 'BC', state: 'CN' },
  { label: 'NB', value: 'NB', state: 'CN' },
  { label: 'NL', value: 'NL', state: 'CN' },
  { label: 'NT', value: 'NT', state: 'CN' },
  { label: 'NS', value: 'NS', state: 'CN' },
  { label: 'NU', value: 'NU', state: 'CN' },
  { label: 'ON', value: 'ON', state: 'CN' },
  { label: 'PE', value: 'PE', state: 'CN' },
  { label: 'SK', value: 'SK', state: 'CN' },
  { label: 'YT', value: 'YT', state: 'CN' },
];

const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};

const JobTypesData = [
  {
    id: 1,
    name: 'General Auto Spa',
    value: 'General Auto Spa',
  },
  {
    id: 2,
    name: 'General Auto Repair',
    value: 'General Auto Repair',
  },
  {
    id: 3,
    name: 'Scheduled Maintenance',
    value: 'Scheduled Maintenance',
    checked: false,
  },
  {
    id: 4,
    name: 'Air & Fuel Delivery',
    value: 'Air & Fuel Delivery',
    checked: false,
  },
  { id: 5, name: 'Brakes', value: 'Brakes', checked: false },
  { id: 6, name: 'Belts & Cooling', value: 'Belts & Cooling', checked: false },
  {
    id: 7,
    name: 'Diagnosis & Inspection',
    value: 'Diagnosis & Inspection',
  },
  {
    id: 8,
    name: 'Electrical & Battery',
    value: 'Electrical & Battery',
  },
  {
    id: 9,
    name: 'Engine',
    value: 'Engine',
  },
  {
    id: 10,
    name: 'Entertainment & Telematics',
    value: 'Entertainment & Telematics',
    checked: false,
  },
  {
    id: 11,
    name: 'Exhaust & Emissions',
    value: 'Exhaust & Emissions',
    checked: false,
  },
  {
    id: 12,
    name: 'Exterior/Interior',
    value: 'Exterior Interior',
  },
  {
    id: 13,
    name: 'Fluids & Accessories',
    value: 'Fluids & Accessories',
    checked: false,
  },
  {
    id: 14,
    name: 'Steering, Suspension & Alignment',
    value: 'Steering, Suspension & Alignment',
    checked: false,
  },
  {
    id: 15,
    name: 'Tranmission, Differential & Transfer Case',
    value: 'Tranmission, Differential & Transfer Case',
    checked: false,
  },
  {
    id: 16,
    name: 'Under Hood',
    value: 'Under Hood',
    checked: false,
  },
  {
    id: 17,
    name: 'Wheels & Tires',
    value: 'Wheels & Tires',
    checked: false,
  },
  {
    id: 18,
    name: 'Ignition & Tune Up ',
    value: 'Ignition & Tune Up ',
    checked: false,
  },
  {
    id: 19,
    name: 'Miscellaneous',
    value: 'Miscellaneous',
    checked: false,
  },
];

// generating ro pdfs path
const generateRoPDFsPath = (roId) => {
  const userData = JSON.parse(localStorage.getItem('userData'));

  const { token } = userData;
  const jwtDecodeToken = jwt.decode(token);

  return `ro/${
    jwtDecodeToken?.businessId ||
    userData?.userStore?.['_id'] ||
    userData.businessId
  }/${roId}/${md5(roId)}`;
};

// Uploading Inspection PDF to S3

const uploadInspectionPdfToS3 = async () => {
  const roData = store.getState()?.ro?.roData?.ro || '';

  if (!roData?.id) return;

  const pdfStream = await pdf(<InspectionPDF data={roData} />).toBlob();

  const config = {
    bucketName: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
    dirName: generateRoPDFsPath(roData.id),
    region: process.env.REACT_APP_AWS_S3_REGION,
    accessKeyId: process.env.REACT_APP_AWS_S3_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_S3_SECRET_ACCESS_KEY,
    s3Url: process.env.REACT_APP_AWS_S3_URL,
  };

  const S3Client = new S3(config);

  let res = '';

  try {
    res = await S3Client.uploadFile(pdfStream, 'inspection');
  } catch (err) {
    Toast('error', 'Error while generating inspection PDF!');
    console.error('Error While uploading Inspection PDF to S3 Bucket!');
  }
  return res?.location || '';
};

// Uploading Estimate PDF to S3

const uploadEstimatePdfToS3 = async () => {
  const roData = store.getState()?.ro?.roData?.ro || '';
  if (!roData?.id) return;

  const { data: { term = '' } = '' } = await getDefaultTerms(
    roData.id,
    'Estimate/Work Order',
  );
  // when authorizing estimate
  const jobs = store.getState()?.estimate?.estimateData?.jobs?.jobs || [];
  const appliedTaxes =
    store.getState()?.estimate?.estimateData?.appliedTaxes || [];

  const estimateTemplate =
    store.getState()?.estimate?.estimateData?.jobs?.estimateTemplate || [];
  const estimate = store.getState()?.ro?.roData?.estimateData || '';

  const estimateTandC = term?.description || '';

  const estimateData = {
    ...roData,
    jobs,
    estimate,
    currencySymbol: getCurrencySymbol() || '$',
    termsAndConditions: estimateTandC,
    addCommasToNumber,
    estimateTemplate,
    appliedTaxes,
  };
  const pdfStream = await pdf(<EstimatePDF data={estimateData} />).toBlob();

  const config = {
    bucketName: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
    dirName: generateRoPDFsPath(roData.id),
    region: process.env.REACT_APP_AWS_S3_REGION,
    accessKeyId: process.env.REACT_APP_AWS_S3_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_S3_SECRET_ACCESS_KEY,
    s3Url: process.env.REACT_APP_AWS_S3_URL,
  };
  const S3Client = new S3(config);

  let res = '';

  try {
    res = await S3Client.uploadFile(pdfStream, 'Estimate-WorkOrder');
  } catch (err) {
    Toast('error', 'Error while generating estimate PDF!');
    console.error('Error While uploading Estimate PDF to S3 Bucket!');
  }
  return res?.location || '';
};

// Uploading Work Order PDF to S3

const uploadWorkOrderPdfToS3 = async () => {
  const roData = store.getState()?.ro?.roData?.ro || '';

  if (!roData?.id) return;

  const jobs = store.getState()?.estimate?.estimateData?.jobs?.jobs || [];

  const estimate = store.getState()?.ro?.roData?.estimateData || '';

  const estimateData = {
    ...roData,
    jobs,
    estimate,
    currencySymbol: getCurrencySymbol() || '$',
  };
  const pdfStream = await pdf(<WorkOrderPDF data={estimateData} />).toBlob();

  const config = {
    bucketName: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
    dirName: generateRoPDFsPath(roData.id),
    region: process.env.REACT_APP_AWS_S3_REGION,
    accessKeyId: process.env.REACT_APP_AWS_S3_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_S3_SECRET_ACCESS_KEY,
    s3Url: process.env.REACT_APP_AWS_S3_URL,
  };
  const S3Client = new S3(config);

  let res = '';

  try {
    res = await S3Client.uploadFile(pdfStream, 'Work-in-Progress');
  } catch (err) {
    Toast('error', 'Error while generating work order PDF!');
    console.error('Error While uploading Work Order PDF to S3 Bucket!');
  }
  return res?.location || '';
};

// Uploading Invoice PDF to S3

const uploadInvoiceePdfToS3 = async () => {
  const roData = store.getState()?.ro?.roData?.ro || '';

  if (!roData?.id) return;

  const { data: { term = '' } = '' } = await getDefaultTerms(
    roData.id,
    'Invoice',
  );

  const {
    jobs = [],
    invoice = '',
    subTotals = '',
  } = store.getState()?.payment?.getApprovedJobsSuccess?.ro || '';
  const totalAppliedTaxes =
    store.getState()?.payment?.getApprovedJobsSuccess?.appliedTaxes || [];
  const { authHistory = [], invoiceTemplate = [] } =
    store.getState()?.payment?.getApprovedJobsSuccess || '';

  const invoiceTandC = term?.description || '';

  const { getPaymentsByRo: payments } = store.getState()?.payment;

  const invoiceData = {
    ...roData,
    jobs,
    invoice: invoice || '',
    currencySymbol: getCurrencySymbol() || '$',
    termsAndConditions: invoiceTandC,
    subTotals,
    invoiceTemplate,
    authHistory,
    addCommasToNumber,
    totalAppliedTaxes,
    payments,
  };
  const pdfStream = await pdf(<InvoicePDF data={invoiceData} />).toBlob();

  const config = {
    bucketName: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
    dirName: generateRoPDFsPath(roData.id),
    region: process.env.REACT_APP_AWS_S3_REGION,
    accessKeyId: process.env.REACT_APP_AWS_S3_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_S3_SECRET_ACCESS_KEY,
    s3Url: process.env.REACT_APP_AWS_S3_URL,
  };
  const S3Client = new S3(config);

  let res = '';

  try {
    res = await S3Client.uploadFile(pdfStream, 'invoice');
  } catch (err) {
    Toast('error', 'Error while generating invoice PDF!');
    console.error('Error While uploading Invoice PDF to S3 Bucket!');
  }

  if (res?.location) {
    store.dispatch({
      type: 'UPDATE_RO_PDF_URLS',
      payload: { url: res?.location, type: 'invoicePdfUrl' },
    });
  }
  return res?.location || '';
};

// generating QuickCheckOut pdf path
const generateQuickCheckOutPDFsPath = (id) => {
  const userData = JSON.parse(localStorage.getItem('userData'));

  const { token } = userData;
  const jwtDecodeToken = jwt.decode(token);

  return `quickCheckOut/${
    jwtDecodeToken?.businessId ||
    userData?.userStore?.['_id'] ||
    userData.businessId
  }/${id}/${md5(id)}`;
};

// Uploading Quick CheckOut PDF to S3

const uploadQuickCheckOutPdfToS3 = async (invcData) => {
  const { invoiceData = '', quickCheckOut = '' } =
    store.getState()?.quickCheckOut?.quickCheckData || '';

  const obj = {
    ...quickCheckOut,
    invoice: { ...(invoiceData || ''), ...invcData },
    currencySymbol: getCurrencySymbol() || '$',
    addCommasToNumber,
  };

  const pdfStream = await pdf(<QuickCheckout data={obj} />).toBlob();

  const config = {
    bucketName: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
    dirName: generateQuickCheckOutPDFsPath(obj.id),
    region: process.env.REACT_APP_AWS_S3_REGION,
    accessKeyId: process.env.REACT_APP_AWS_S3_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_S3_SECRET_ACCESS_KEY,
    s3Url: process.env.REACT_APP_AWS_S3_URL,
  };
  const S3Client = new S3(config);

  S3Client.uploadFile(pdfStream, 'quickcheckout')
    .then((data) => {
      store.dispatch({
        type: 'UPDATE_RO_PDF_URLS',
        payload: { url: data?.location, type: 'quickCheckOutPdfUrl' },
      });
    })
    .catch(() =>
      console.error('Error While uploading Quick Checkout PDF to S3 Bucket!'),
    );
};

// generating estimator pdf path
const generateEstimatorPDFsPath = (estimateId) => {
  const userData = JSON.parse(localStorage.getItem('userData'));

  const { token } = userData;
  const jwtDecodeToken = jwt.decode(token);

  return `estimator/${process.env.REACT_APP_ENV}/${
    jwtDecodeToken?.businessId ||
    userData?.userStore?.['_id'] ||
    userData.businessId
  }/${estimateId}/${md5(estimateId)}`;
};

// Uploading Estimator PDF to S3

const uploadEstimatorPdfToS3 = async (pdfData) => {
  const { data: { term = '' } = '' } = await getDefaultTerms(
    null,
    'Estimate/Work Order',
    true,
  );

  const estimateTandC = term?.description || '';

  pdfData.termsAndConditions = estimateTandC;
  pdfData.addCommasToNumber = addCommasToNumber;

  const pdfStream = await pdf(<EstimatorPDF data={pdfData} />).toBlob();

  const config = {
    bucketName: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
    dirName: generateEstimatorPDFsPath(pdfData.estimateId),
    region: process.env.REACT_APP_AWS_S3_REGION,
    accessKeyId: process.env.REACT_APP_AWS_S3_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_S3_SECRET_ACCESS_KEY,
    s3Url: process.env.REACT_APP_AWS_S3_URL,
  };
  const S3Client = new S3(config);

  let res = '';

  try {
    res = await S3Client.uploadFile(pdfStream, 'estimator');
  } catch (err) {
    Toast('error', 'Error while generating Estimator PDF!');
    console.error('Error While uploading Estimator PDF to S3 Bucket!');
  }
  return res?.location || '';
};

// generating media attachments path
const generatePathForRoMediaAttachments = (roId, uid) => {
  const userData = JSON.parse(localStorage.getItem('userData'));

  const { token } = userData;
  const jwtDecodeToken = jwt.decode(token);

  return `mediaAttachments/ro/${
    jwtDecodeToken?.businessId ||
    userData?.userStore?.['_id'] ||
    userData.businessId
  }/${roId}/${uid}`;
};

// Uploading RO Media Attachments to S3

const uploadRoMediaAttachmentsToS3 = async (attachments, roId) => {
  // const roData = store.getState()?.ro?.roData?.ro || '';
  // const roId =  roData?.id || '';
  if (!roId) return;
  const id = uuid();

  const promises = [];

  const config = {
    bucketName: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
    dirName: generatePathForRoMediaAttachments(roId, id),
    region: process.env.REACT_APP_AWS_S3_REGION,
    accessKeyId: process.env.REACT_APP_AWS_S3_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_S3_SECRET_ACCESS_KEY,
    s3Url: process.env.REACT_APP_AWS_S3_URL,
  };
  const S3Client = new S3(config);

  attachments?.map((attachment) => {
    promises.push(
      S3Client.uploadFile(
        attachment.file,
        attachment.file?.name?.split('.')?.[0],
      ),
    );
  });

  let res = '';
  try {
    await Promise.all(promises);

    // also add uuid of attachments to mysql table

    const {
      data: { data },
    } = await addMediaAttachments({
      roId,
      uuid: id,
      count: attachments?.length || 0,
    });
    res = data;
  } catch (err) {
    Toast(
      'error',
      err?.message || 'Error while uploading RO Attachments to S3!',
    );
  }
  return res;
};

// Uploading Business Profile Image to S3

const uploadBusinessProfileImageToS3 = async (attachment) => {
  const tokenObj = JSON.parse(localStorage.getItem('userData'));
  let userId = '';
  if (tokenObj) {
    userId = tokenObj?.userStore?.userId || tokenObj?.userId || '';
  }

  if (!userId) return;

  const dirName = `businessProfileImages/${
    process.env.REACT_APP_ENV
  }/${userId}/${md5(userId)}`;
  const config = {
    bucketName: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
    dirName,
    region: process.env.REACT_APP_AWS_S3_REGION,
    accessKeyId: process.env.REACT_APP_AWS_S3_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_S3_SECRET_ACCESS_KEY,
    s3Url: process.env.REACT_APP_AWS_S3_URL,
  };
  const S3Client = new S3(config);

  let res = '';

  try {
    res = await S3Client.uploadFile(attachment, 'businessProfileImage');
  } catch (err) {
    console.log('Error While Uploading Business Profile Image!');
  }
  return res;
};

// Uploading Account Profile Image to S3

const uploadAccountProfileImageToS3 = async (attachment, imageLink) => {
  const tokenObj = JSON.parse(localStorage.getItem('userData'));
  let userId = '';
  if (tokenObj) {
    userId = tokenObj?.userStore?.userId || '';
  }

  if (!userId) return;

  let _uuid = imageLink?.split('/')?.[7] || '';
  if (!_uuid) {
    _uuid = uuid();
  }
  const dirName = `accountProfileImages/${process.env.REACT_APP_ENV}/${userId}/${_uuid}`;
  const config = {
    bucketName: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
    dirName,
    region: process.env.REACT_APP_AWS_S3_REGION,
    accessKeyId: process.env.REACT_APP_AWS_S3_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_S3_SECRET_ACCESS_KEY,
    s3Url: process.env.REACT_APP_AWS_S3_URL,
  };
  const S3Client = new S3(config);

  let res = '';

  try {
    res = await S3Client.uploadFile(attachment, 'accountProfileImage');
  } catch (err) {
    console.log('Error While Uploading Account Profile Image!');
  }

  return res;
};

// Uploading PO Invoice Image to S3

const uploadPoInvoiceImageToS3 = async (attachment, imageLink) => {
  const tokenObj = JSON.parse(localStorage.getItem('userData'));
  let userId = '';
  if (tokenObj) {
    userId = tokenObj?.userStore?.userId || '';
  }

  if (!userId) return;

  let _uuid = imageLink?.split('/')?.[7] || '';
  if (!_uuid) {
    _uuid = uuid();
  }
  const dirName = `poInvoiceImages/${process.env.REACT_APP_ENV}/${userId}/${_uuid}`;

  const config = {
    bucketName: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
    dirName,
    region: process.env.REACT_APP_AWS_S3_REGION,
    accessKeyId: process.env.REACT_APP_AWS_S3_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_S3_SECRET_ACCESS_KEY,
    s3Url: process.env.REACT_APP_AWS_S3_URL,
  };
  const S3Client = new S3(config);

  let res = '';

  try {
    res = await S3Client.uploadFile(attachment, 'poInvoiceImage');
  } catch (err) {
    console.log('Error While Uploading PO Invoice Image!');
  }

  return res;
};

const handleNumbers = (value, limit) => {
  /* after using this function value must be converted into Number before using it/sending to api
     because state can contain a value like (10. -> adecimal point after value) */
  let check = true;
  const arr = value?.toString()?.split('.') || [];
  if (arr.length === 2) {
    if (arr[1] === '' || arr[1] === '0') {
      check = false;
    }
  }

  if (value === '-') {
    value = '-';
  } else if (value === '0-') {
    value = '-';
  } else if (check || +value === limit) {
    value = Math.round(value * 100) / 100 || 0;
  }
  return value;
};

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const convertToPNG = (file) => {
  if (!file) return;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, img.width, img.height);

        canvas.toBlob(
          (blob) => {
            resolve(blob);
          },
          'image/png',
          1,
        );
      };

      img.src = e.target.result;
    };

    reader.onerror = (error) => reject(error);

    reader.readAsDataURL(file);
  });
};

const getUserRoleId = () => {
  const userDataa = JSON.parse(localStorage.getItem('userData'));
  if (userDataa) {
    const { token: Token } = userDataa;
    if (Token) {
      const jwtDecodeToken = jwt.decode(Token);
      const role = jwtDecodeToken.roleId;
      return role || '';
    }
  }
};

// subscriptions / active plan
const checkInSubscription = (resource) => {
  const activePlan = store.getState()?.subscription?.subscription || '';

  if (activePlan?.[resource]?.allow) return true;
  return false;
};

// roles and permissions
const hasPermission = (resource, items) => {
  const roleId = getUserRoleId();
  if (roleId === 4) return 'owner'; // when user is owner;

  const permissions = store.getState()?.coUser?.userData?.permissions || [];

  const permissionFound = permissions?.find((p) => p.name === resource);

  if (!permissionFound) return;

  const list = permissionFound?.list || [];

  if (
    resource === 'dashboard' ||
    resource === 'jobboard' ||
    resource === 'appointments' ||
    resource === 'listing' ||
    resource === 'reports' ||
    resource === 'timeReport' ||
    resource === 'quickcheckout' ||
    resource === 'purchases' ||
    resource === 'estimator' ||
    resource === 'communication'
  ) {
    if (
      items === 'isAllowReports' ||
      items === 'isAllowQuickcheckout' ||
      items === 'isAllowPo' ||
      items === 'isAllowCommunication' ||
      items === 'isAllowEstimator' ||
      items === 'isAllowTimeReport'
    ) {
      return permissionFound?.value || false;
    }

    const found = list?.find((p) => p.key === items);

    return found?.value || false;
  }

  if (resource === 'settings') {
    if (items?.parent === 'RO Settings') {
      const found = list?.find((p) => p.key === 'RO Settings');
      const childList = found?.list || [];

      const childFound = childList?.find((p) => p.key === items.child);

      return childFound?.value || false;
    }
    const found = list?.find((p) => p.key === items);
    return found?.value || false;
  }

  if (resource === 'repairOrder') {
    if (items === 'isAllowRo') {
      return permissionFound?.value || false;
    }

    if (
      items === 'Log' ||
      items === 'Customer Stats' ||
      items === 'Inspection' ||
      items === 'Estimate/Work Order' ||
      items === 'Work In Progress' ||
      items === 'Payment & Invoice' ||
      items === 'Tech Recommendation' ||
      items === 'Communication' ||
      items === 'Create' ||
      items === 'Edit' ||
      items === 'Void Transaction' ||
      items === 'Issue Refund' ||
      items === 'Close RO' ||
      items === 'Reactivate RO' ||
      items === 'Send to A/R'
    ) {
      const found = list?.find((p) => p.key === items);
      return found?.value || false;
    }

    if (items.parent === 'Communication') {
      const found = list?.find((p) => p.key === 'Communication');
      const childList = found?.list || [];

      const childFound = childList?.find((p) => p.key === items.child);

      return childFound?.value || false;
    }

    if (items.parent === 'Work Order') {
      const found = list?.find((p) => p.key === 'Work In Progress');
      const childList = found?.list || [];

      const childFound = childList?.find((p) => p.key === items.child);

      return childFound?.value || false;
    }

    if (items.parent === 'Estimate') {
      const found = list?.find((p) => p.key === 'Estimate/Work Order');
      const childList = found?.list || [];

      const childFound = childList?.find((p) => p.key === items.child);

      return childFound?.value || false;
    }

    if (items.parent === 'Inspection') {
      const found = list?.find((p) => p.key === 'Inspection');
      const childList = found?.list || [];

      const childFound = childList?.find((p) => p.key === items.child);

      return childFound?.value || false;
    }

    if (items.parent === 'Tech Recommendation') {
      const found = list?.find((p) => p.key === 'Tech Recommendation');
      const childList = found?.list || [];

      const childFound = childList?.find((p) => p.key === items.child);

      return childFound?.value || false;
    }
  }
};

const SearchDebounce = (func, setSearchValue) => {
  let timer;
  return function (...args) {
    if (args[2] === 'phone') {
      const regex = /^(^$|[0-9*+-]?\s*)+$/;
      const isValid = regex.test(args[1]);
      if (!isValid) {
        return;
      }
      setSearchValue(args[1]);
    }

    if (args[2] === 'name') {
      const regex = /^(^$|[a-zA-Z]+,?\s*)+$/;
      const isValid = regex.test(args[1]);
      if (!isValid) {
        return;
      }
      setSearchValue(args[1]);
    }

    if (args[2] === 'plate') {
      setSearchValue(args[1]);
    }

    const context = this;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      func.apply(context, args);
    }, 500);
  };
};

const isTrialEnds = () => {
  const trialInfo = store.getState()?.subscription?.trialInfo || '';

  if (trialInfo?.status === 'trialing') return false;
  return true;
};

const getRemainingDays = () => {
  const trialEndDate =
    store.getState()?.subscription?.trialInfo?.current_period_end || '';

  if (!trialEndDate) return '-';

  const timeleft = trialEndDate * 1000 - new Date().getTime();
  return Math.ceil(timeleft / 86400000);
};

// subscription -> plans button text
const getPlansButtonTxt = (currentPlanPrecedence) => {
  const trialInfo = store.getState()?.subscription?.trialInfo || '';
  const subscribedPlan = store.getState()?.subscription?.subscribedPlan || '';

  const subscribedPlanPrecedence = subscribedPlan?.precedence || '';

  if (trialInfo?.status === 'trialing') {
    return 'Pay Now';
  }

  if (currentPlanPrecedence === subscribedPlanPrecedence) {
    return 'Activated';
  }

  if (currentPlanPrecedence > subscribedPlanPrecedence) {
    return 'Upgrade';
  }
  return '';
};

// generating ro pdfs path
const generateReportsPDFsPath = (reportName) => {
  const userData = JSON.parse(localStorage.getItem('userData'));

  const { token } = userData;
  const jwtDecodeToken = jwt.decode(token);

  return `report/${
    jwtDecodeToken?.businessId ||
    userData?.userStore?.['_id'] ||
    userData.businessId
  }/${reportName}`;
};
const uploadReportsPdfToS3 = async (data) => {
  const config = {
    bucketName: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
    dirName: generateReportsPDFsPath(data?.reportName),
    region: process.env.REACT_APP_AWS_S3_REGION,
    accessKeyId: process.env.REACT_APP_AWS_S3_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_S3_SECRET_ACCESS_KEY,
    s3Url: process.env.REACT_APP_AWS_S3_URL,
  };
  const S3Client = new S3(config);

  let res = '';

  try {
    res = await S3Client.uploadFile(data?.pdfStream, moment().unix());
  } catch (err) {
    Toast('error', 'Error while generating Day End PDF!');
    console.error('Error While uploading Day End PDF to S3 Bucket!');
  }

  return res?.location || '';
};

const uploadRoSignatureToS3 = async (attachment, roId, userId) => {
  if (!userId) return;

  const dirName = `roSignatures/${process.env.REACT_APP_ENV}/${userId}/${roId}`;

  const config = {
    bucketName: process.env.REACT_APP_AWS_S3_BUCKET_NAME,
    dirName,
    region: process.env.REACT_APP_AWS_S3_REGION,
    accessKeyId: process.env.REACT_APP_AWS_S3_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_S3_SECRET_ACCESS_KEY,
    s3Url: process.env.REACT_APP_AWS_S3_URL,
  };
  const S3Client = new S3(config);

  let res = '';

  try {
    res = await S3Client.uploadFile(attachment, 'roSignature');
  } catch (err) {
    console.log('Error While Uploading RO Signature Image!');
  }

  return res;
};

const roStages = () => {
  return [
    {
      id: 1,
      name: 'Work Not Started',
      value: 'Work Not Started',
      icon: <WorkNotStartedIcon />,
      color: '#FF4A55',
      bgColor: 'rgba(255, 54, 54, 0.25)',
      jobBoardStatusId: 1,
    },
    {
      id: 2,
      name: 'Inspection',
      value: 'Inspection',
      icon: <InspectionIcon />,
      color: '#3A87FD',
      bgColor: 'rgba(58, 135, 253, 0.25)',
      jobBoardStatusId: 2,
    },
    {
      id: 3,
      name: 'Estimate',
      value: 'Estimate',
      icon: <EstimateIcon />,
      color: '#F38829',
      bgColor: 'rgba(255, 96, 25, 0.25)',
      jobBoardStatusId: 2,
    },
    {
      id: 4,
      name: 'Awaiting Authorization',
      value: 'Awaiting Authorization',
      icon: <AwaitingAuthorizationIcon />,
      color: '#6921C4',
      bgColor: 'rgba(94, 48, 235, 0.25)',
      jobBoardStatusId: 2,
    },
    {
      id: 5,
      name: 'Awaiting Parts',
      value: 'Awaiting Parts',
      icon: <AwaitingPartsIcon />,
      color: '#ADA401',
      bgColor: 'rgba(251, 237, 0, 0.25)',
      jobBoardStatusId: 3,
    },
    {
      id: 6,
      name: 'Declined',
      value: 'Declined',
      icon: <DeclinedIcon />,
      color: '#FF3636',
      bgColor: 'rgba(255, 54, 54, 0.25)',
      jobBoardStatusId: 2,
    },
    {
      id: 7,
      name: 'In Progress',
      value: 'In Progress',
      icon: <InProgressIcon />,
      color: '#35C677',
      bgColor: 'rgba(53, 198, 119, 0.25)',
      jobBoardStatusId: 3,
    },
    {
      id: 8,
      name: 'Ready',
      value: 'Ready',
      icon: <ReadyIcon />,
      color: '#76BB40',
      bgColor: 'rgba(60, 209, 23, 0.25)',
      jobBoardStatusId: 4,
    },
    {
      id: 9,
      name: 'On Hold',
      value: 'On Hold',
      icon: <OnHoldIcon />,
      color: '#ACACAC',
      bgColor: 'rgba(172, 172, 172, 0.25)',
      jobBoardStatusId: 3,
    },
  ];
};

const calculateDateRanges = (value) => {
  console.log('***calculateDateRanges****', value);
  if (value === 'today') {
    return [moment().toDate(), moment().toDate()];
  }
  if (value === 'lastWeek') {
    const startDate = moment()
      .subtract(moment().day(), 'days')
      .subtract(6, 'days')
      .toDate();
    const endDate = moment().subtract(moment().day(), 'days').toDate();
    return [startDate, endDate];
  }
  if (value === 'lastMonth') {
    const startDate = moment()
      .month(moment().month() - 1)
      .startOf('month')
      .toDate();
    const endDate = moment()
      .month(moment().month() - 1)
      .endOf('month')
      .toDate();

    return [startDate, endDate];
  }
};

const replaceSmsTags = (templateString = '', data) => {
  if (!templateString?.trim()) return '';
  // Regular expression to find all tags in the string
  const tagRegex = /\{(\w+)\}/g;

  // Replace each tag in the string with its corresponding value from the data object
  return templateString?.replace(tagRegex, (match, tagName) => {
    return tagName in data ? data[tagName] : match;
  });
};

const smsAlert = (message) => {
  Swal.fire({
    title: 'Alert',
    text: message,
    icon: 'warning',
    confirmButtonText: 'ok   ',

    customClass: {
      container: 'CustomizeSwalAlert',
      popup: 'CustomizeSwalPopup',
      icon: 'CustomizeSwalIcon',
      title: 'CustomizeSwalTitle',
      confirmButton: 'CustomizeSwalConfirmButton',
      cancelButton: 'CustomizeSwalCancelButton',
    },
  });
};

const checkMessageLinks = (messageContent) => {
  const restrictedUrls = ['bit.ly', 'tinyurl.com', 'goo.gl', 't2mio.com'];
  const urlSearched = linkify.find(messageContent.toLowerCase());
  const checkShortUrl = urlSearched.some(
    (item) =>
      item.type === 'url' &&
      restrictedUrls.some((restrictedUrl) =>
        item.value.includes(restrictedUrl),
      ),
  );

  if (checkShortUrl) {
    smsAlert(
      'Your message contains a public URL. For privacy and security reasons, public URLs are not allowed in SMS messages. Please remove the URL and send/save the message again.',
    );
    return true;
  }
  return false;
};

const getWorkedTimeColor = (workedTimeInSeconds, totalHours) => {
  // Convert worked time from seconds to hours
  const workedTimeInHours = workedTimeInSeconds / 3600;
  const percentage = (workedTimeInHours / totalHours || 0) * 100;

  if (percentage <= 0) {
    return 'grey';
  }
  if (percentage <= 50) {
    return 'red'; // 0 to 50% of total hours
  }
  if (percentage <= 70) {
    return 'yellow'; // 50% to 70% of total hours
  }
  // 70% to 100% or above of total hours
  return 'green';
};

const formatSeconds = (seconds) => {
  seconds = +seconds || 0;

  // Convert seconds to hours, minutes, and seconds
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  // Format the result to be `hh:mm:ss`
  return `${hrs.toString().padStart(2, '0')}:${mins
    .toString()
    .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const secondsToHM = (seconds) => {
  seconds = +seconds || 0;

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return { hours, minutes };
};

const calculateWorkInProgressBarData = (jobs = []) => {
  // calculate only for  labors
  let totalHours = 0;
  let completedHours = 0;

  // Calculate total and completed hours for approved jobs
  jobs?.forEach((job) => {
    const labors = job?.extrasWithJob?.items?.filter(
      (item) => item?.type === 'labours',
    );

    const laborTotalHours = labors?.reduce(
      (sum, labor) => sum + (+labor?.qty || 0),
      0,
    );

    if (job.jobStatus === 'completed') {
      completedHours += laborTotalHours || 0;
    }
    if (
      job.authorizationStatus?.toLowerCase() === 'approved' &&
      labors?.length
    ) {
      totalHours += laborTotalHours || 0;
    }
  });
  const hasApprovedJobs = jobs?.some(
    (job) => job.authorizationStatus?.toLowerCase() === 'approved',
  );

  // Avoid division by zero
  if (totalHours === 0) {
    return { percentage: 0, color: 'grey' };
  }

  // Calculate percentage
  const percentage = (completedHours / totalHours) * 100;

  // Determine color based on percentage
  let color;
  if (percentage <= 0) {
    color = 'gray';
  } else if (percentage <= 50) {
    color = 'red';
  } else if (percentage <= 70) {
    color = 'yellow';
  } else {
    color = 'green';
  }

  return { percentage, color, totalHours, completedHours, hasApprovedJobs };
};

export {
  addCommasToNumber,
  makingLabourTimeGuideData,
  convertSQLDateToLocalDate,
  getCurrentDateAndTime,
  calculateDateRange,
  decodeToken,
  unitedStates,
  validateEmail,
  JobTypesData,
  uploadInspectionPdfToS3,
  uploadEstimatePdfToS3,
  uploadWorkOrderPdfToS3,
  uploadInvoiceePdfToS3,
  uploadRoMediaAttachmentsToS3,
  uploadBusinessProfileImageToS3,
  uploadAccountProfileImageToS3,
  uploadQuickCheckOutPdfToS3,
  uploadEstimatorPdfToS3,
  uploadPoInvoiceImageToS3,
  handleNumbers,
  toBase64,
  convertToPNG,
  checkInSubscription,
  hasPermission,
  getUserRoleId,
  SearchDebounce,
  isTrialEnds,
  getRemainingDays,
  getPlansButtonTxt,
  uploadReportsPdfToS3,
  uploadRoSignatureToS3,
  roStages,
  calculateDateRanges,
  replaceSmsTags,
  checkMessageLinks,
  smsAlert,
  formatSeconds,
  secondsToHM,
  getWorkedTimeColor,
  calculateWorkInProgressBarData,
};
